// Utilitarios das telas do Processo competitivo (venda de ativos com proposta lacrada).
import axios from "axios";
import { registerBadgeSet } from "@/components/ui/badge-statuses";
import { chunkedUpload } from "@/common/util/chunked-upload";

export { errorMessage, formatBytes, formatDate, notifyError, notifySuccess, notifyWarning, openUrl } from "@/components/rma/rma.util";

export const API = "/api/admin/competitive";

registerBadgeSet("cp-process", {
  draft: ["Rascunho", "neutral"],
  in_progress: ["Em andamento", "success"],
  closed: ["Encerrado", "primary"],
  cancelled: ["Cancelado", "neutral"],
});

registerBadgeSet("cp-asset", {
  available: ["Disponível", "success"],
  blocked: ["Bloqueado", "warning"],
  sold: ["Arrematado", "primary"],
  withdrawn: ["Retirado", "neutral"],
});

registerBadgeSet("cp-stage", {
  planned: ["Planejada", "neutral"],
  cancelled: ["Cancelada", "danger"],
});

registerBadgeSet("cp-registration", {
  draft: ["Rascunho", "neutral"],
  submitted: ["Em análise", "warning"],
  correction: ["Correção solicitada", "info"],
  approved: ["Habilitado", "success"],
  rejected: ["Inabilitado", "danger"],
  withdrawn: ["Desistência", "neutral"],
});

registerBadgeSet("cp-registration-doc", {
  pending: ["Aguardando conferência", "warning"],
  approved: ["Aprovado", "success"],
  rejected: ["Recusado", "danger"],
});

// Carimbos do banco (created_at, submitted_at...) sao UTC; mostra em Brasilia.
export function formatStamp(value, { seconds = false } = {}) {
  if (!value) return "—";
  const date = new Date(String(value).includes("T") ? value : `${String(value).replace(" ", "T")}Z`);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", ...(seconds ? { second: "2-digit" } : {}) });
}

export const GROUP_KIND_PILLS = [
  { value: "basket", text: "Cestas" },
  { value: "lot", text: "Lotes" },
  { value: "block", text: "Blocos únicos" },
];

const money = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });

export function formatMoney(value) {
  if (value === null || value === undefined || value === "") return "—";
  return money.format(Number(value) || 0);
}

// R$ 356,08 mi / R$ 12,5 mil, para os indicadores.
export function formatMoneyShort(value) {
  const number = Number(value) || 0;
  const abs = Math.abs(number);
  if (abs >= 1e9) return `R$ ${(number / 1e9).toLocaleString("pt-BR", { maximumFractionDigits: 2 })} bi`;
  if (abs >= 1e6) return `R$ ${(number / 1e6).toLocaleString("pt-BR", { maximumFractionDigits: 2 })} mi`;
  if (abs >= 1e3) return `R$ ${(number / 1e3).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} mil`;
  return formatMoney(number);
}

export function formatPercent(value) {
  return `${(Number(value) || 0).toLocaleString("pt-BR", { maximumFractionDigits: 2 })}%`;
}

// Valor do formulario: aceita "1.234,56"; mostra o numero gravado com virgula.
export function moneyInput(value) {
  if (value === null || value === undefined || value === "") return "";
  return (Number(value) || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Mascara de valor com 2 casas (R$, area): os digitos digitados viram centavos,
// "123456" -> "1.234,56".
export function maskDecimal(value) {
  const digits = String(value === null || value === undefined ? "" : value).replace(/\D/g, "").replace(/^0+(?=\d)/, "").slice(0, 15);
  if (!digits) return "";
  return (Number(digits) / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Mascara de coordenada em graus decimais: sinal opcional, ate `intDigits`
// digitos inteiros, virgula e ate 7 casas ("-28,6383000").
export function maskCoordinate(value, intDigits) {
  let text = String(value === null || value === undefined ? "" : value).trim().replace(/\./g, ",");
  const negative = text.startsWith("-");
  text = text.replace(/[^\d,]/g, "");
  const [whole, ...rest] = text.split(",");
  let out = whole.slice(0, intDigits);
  if (rest.length) out += `,${rest.join("").slice(0, 7)}`;
  return `${negative ? "-" : ""}${out}`;
}

// Cronograma em horario civil de Brasilia ("YYYY-MM-DD HH:mm:ss", sem fuso).
export function formatSchedule(value) {
  if (!value) return "—";
  const text = String(value);
  return `${text.slice(8, 10)}/${text.slice(5, 7)}/${text.slice(0, 4)}, ${text.slice(11, 16)}`;
}

// "YYYY-MM-DD HH:mm:ss" <-> valor do <input type="datetime-local">.
export function toInputDateTime(value) {
  return value ? String(value).slice(0, 16).replace(" ", "T") : "";
}

export function nowBrasilia() {
  const parts = new Intl.DateTimeFormat("sv-SE", { timeZone: "America/Sao_Paulo", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" }).format(new Date());
  return parts.replace("T", " ");
}

// Dias entre agora e o marco (para "hoje", "em 93 dias").
export function relativeDays(value) {
  if (!value) return "";
  const target = new Date(String(value).slice(0, 10) + "T00:00:00");
  const today = new Date(nowBrasilia().slice(0, 10) + "T00:00:00");
  const days = Math.round((target - today) / 86400000);
  if (days === 0) return "hoje";
  if (days === 1) return "amanhã";
  if (days > 1) return `em ${days} dias`;
  return days === -1 ? "ontem" : `há ${-days} dias`;
}

// Processo escolhido nas telas (Ativos, Cestas e lotes, Etapas, Painel): fica
// na URL (?processo=) e no navegador para a proxima tela abrir no mesmo.
const STORAGE_KEY = "competitive.process";

export function rememberProcess(id) {
  try {
    if (id) localStorage.setItem(STORAGE_KEY, String(id));
    else localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    // navegador sem storage: so nao lembra
  }
}

export function rememberedProcess() {
  try {
    return Number(localStorage.getItem(STORAGE_KEY)) || null;
  } catch (error) {
    return null;
  }
}

// Envia o arquivo em partes pela API e devolve { uploaded_key, file_name } para o cadastro.
export async function uploadForRecord(file, onProgress) {
  const result = await chunkedUpload(file, { onProgress: (percent) => onProgress && onProgress(percent) });
  return { uploaded_key: result.key, file_name: file.name };
}

let optionsCache = null;
export async function loadOptions() {
  if (!optionsCache) {
    optionsCache = axios.get(`${API}/options`).then((result) => result.data.content).catch((error) => {
      optionsCache = null;
      throw error;
    });
  }
  return optionsCache;
}
